import { Button, ButtonGroup, Segment } from 'semantic-ui-react';
import Alert from '../../../../../utils/alert';
import CloneUtils from '../../../../../utils/cloneUtils';
import ImportsRepo from '../../../../../utils/repository/importsRepo';
import {
    ROW_MAPPING_TARGET,
    ROW_MAPPING_TYPE,
    TEMPORARY_DESCRIPTOR_ID_PREFIX,
    TEMPORARY_ROW_ID_PREFIX,
} from '../../constants/generalConstants';
import useModalState from '../../../../../hooks/useModalState';
import useMappingStore from '../../stores/mappingStore';
import { ExtractionType, MappingsType, MasterDescriptorsMapType, RowMappingType } from '../../types';
import { ensureEmptyMapping, getIsMappingEmpty } from './utils';
import MappingsSaveModal from './components/mappingsSaveModal';
import MappingsTable from './components/mappingsTable';
import { MASTER_DESCRIPTOR_OVER_PREFIX } from './constants';
import { useState } from 'react';

type StrategyHandlerType = (
    sourceIndex: number,
    targetIndex: number,
    source: RowMappingType,
    target: RowMappingType,
    mappings: MappingsType
) => void;

type MappingsPropsType = {
    extraction: ExtractionType;
    handleDeleteImport: () => void;
    setExtraction: (extraction: ExtractionType) => void;
    masterDescriptorsMap: MasterDescriptorsMapType;
};
export default function Mappings({
    extraction,
    handleDeleteImport,
    setExtraction,
    masterDescriptorsMap,
}: MappingsPropsType) {
    const {
        confidencesMap,
        draggedItemId,
        mappings,
        draggedOverItemId,
        setDraggedItemId,
        setMappings,
        setDraggedOverItemId,
    } = useMappingStore();
    const saveModalState = useModalState();
    const [loading, setLoading] = useState(false);

    const resetDraggableState = () => {
        setDraggedItemId(undefined);
        setDraggedOverItemId(undefined);
    };

    const handleImportDescriptorOnDropSwap = () => {
        if (!draggedOverItemId || !draggedItemId) {
            return;
        }

        const source = mappings.find((mapping) => mapping.rowId === draggedItemId);
        if (!source) {
            resetDraggableState();
            return;
        }

        const target = mappings.find((mapping) => mapping.descriptorId === draggedOverItemId);
        if (!target) {
            resetDraggableState();
            return;
        }

        let newMappings = mappings.map((mapping) => {
            if (mapping.rowId === draggedItemId) {
                // source case
                return {
                    ...source,
                    rowId: target.rowId,
                    alternatives: CloneUtils.deepClone(target.alternatives),
                    confidence: confidencesMap[target.rowId]?.[source.descriptorId] ?? 0,
                };
            } else if (mapping.descriptorId === draggedOverItemId) {
                // target case
                return {
                    ...target,
                    rowId: source.rowId,
                    alternatives: CloneUtils.deepClone(source.alternatives),
                    confidence: confidencesMap[source.rowId]?.[target.descriptorId] ?? 0,
                };
            } else {
                return mapping;
            }
        });

        newMappings = newMappings.filter((mapping) => !getIsMappingEmpty(mapping));
        ensureEmptyMapping(newMappings);
        setMappings(newMappings);
        resetDraggableState();
    };

    /**
     *
     * @param strategyHandler Executes the mapping strategy and MUTATES the passed collection
     */
    const baseMasterDescriptorStrategyHandler = (strategyHandler: StrategyHandlerType) => {
        if (!draggedOverItemId || !draggedItemId) {
            return;
        }

        const source = mappings.find((mapping) => mapping.descriptorId === draggedItemId);
        if (!source) {
            resetDraggableState();
            return;
        }

        const target = mappings.find(
            (mapping) => mapping.descriptorId === draggedOverItemId.replace(MASTER_DESCRIPTOR_OVER_PREFIX, '')
        );
        if (!target) {
            resetDraggableState();
            return;
        }

        const sourceIndex = mappings.findIndex((mapping) => mapping.descriptorId === source.descriptorId);
        if (sourceIndex < 0) {
            resetDraggableState();
            return;
        }

        const targetIndex = mappings.findIndex((mapping) => mapping.descriptorId === target.descriptorId);
        if (targetIndex < 0) {
            resetDraggableState();
            return;
        }

        const newMappings: MappingsType = CloneUtils.deepClone(mappings);

        strategyHandler(sourceIndex, targetIndex, source, target, newMappings);

        setMappings(newMappings);
        resetDraggableState();
    };

    const handleMasterDescriptorOnDropSwap = () => {
        baseMasterDescriptorStrategyHandler((sourceIndex, targetIndex, source, target, mappings) => {
            mappings[sourceIndex] = target;
            mappings[targetIndex] = source;
        });
    };

    const handleMasterDescriptorOnDropInsert = () => {
        baseMasterDescriptorStrategyHandler((sourceIndex, targetIndex, source, target, mappings) => {
            mappings.splice(sourceIndex, 1);
            const insertIndex = sourceIndex < targetIndex ? targetIndex : targetIndex + 1;
            mappings.splice(insertIndex, 0, source);
        });
    };

    const handleMappingsSave = () => {
        setLoading(true);
        saveModalState.closeModal();
        const mappingsForUpdate = mappings
            .map((x) => {
                const rowId = x.rowId.includes(TEMPORARY_ROW_ID_PREFIX) ? null : x.rowId;
                const descriptorId = x.descriptorId.includes(TEMPORARY_DESCRIPTOR_ID_PREFIX) ? null : x.descriptorId;
                const alternatives = x.alternatives.filter(
                    (x) => x.target !== ROW_MAPPING_TARGET.SELF && x.type !== ROW_MAPPING_TYPE.INITIAL
                );
                return {
                    ...x,
                    descriptorId,
                    rowId,
                    alternatives,
                };
            })
            .filter((x) => x.rowId !== null || x.descriptorId !== null);

        ImportsRepo.mappingsUpdate(
            extraction.id,
            { mappings: mappingsForUpdate },
            (res: { data: ExtractionType }) => {
                setExtraction(res.data);
                Alert.success('Mappings successfully saved');
                setLoading(false);
            },
        );
    };

    return (
        <Segment>
            <Segment>
                <div className="overflow--auto">
                    <MappingsTable
                        handleImportDescriptorOnDropSwap={handleImportDescriptorOnDropSwap}
                        handleMasterDescriptorOnDropInsert={handleMasterDescriptorOnDropInsert}
                        handleMasterDescriptorOnDropSwap={handleMasterDescriptorOnDropSwap}
                        importDescriptors={extraction.importTable.descriptors}
                        masterDescriptorsMap={masterDescriptorsMap}
                        reports={extraction.importTable.reports}
                    />
                </div>
            </Segment>

            <ButtonGroup fluid>
                <Button disabled={loading} loading={loading} primary onClick={saveModalState.openModal}>
                    Save
                </Button>
                <Button negative onClick={handleDeleteImport}>
                    Delete
                </Button>
            </ButtonGroup>
            <MappingsSaveModal
                open={saveModalState.open}
                onClose={saveModalState.closeModal}
                handleSave={handleMappingsSave}
            />
        </Segment>
    );
}
